/**
 * Price series utilities.
 * Pure functions for computing direction-based colors and realtime dot positions.
 * No React dependencies.
 */

import {
  CANDLESTICK_COLORS,
  DEFAULT_CHART_COLOR,
  getDirectionLineColor,
  getPriceDirection,
} from "./colorPalette";
import { getYValue } from "./pathGenerators";
import type {
  ChartLayout,
  ChartMode,
  OhlcvPoint,
  PricePoint,
  Scale,
  TickerSeries,
} from "~types/types";

// ==================== Types ====================

export interface DirectionColorOptions {
  /** Chart mode (single or comparison) */
  mode: ChartMode;
  /** Visible data points */
  visibleData: (PricePoint | OhlcvPoint)[];
  /** Series metadata (for previousClose and color) */
  seriesMeta?: TickerSeries;
  /** Fallback color if provided (used in comparison mode) */
  providedColor?: string;
  /** Use previousClose as baseline for direction (default: false, uses first visible point) */
  usePreviousCloseAsBaseline?: boolean;
}

export interface RealtimeDotPosition {
  /** X position of the dot */
  x: number;
  /** Y position of the dot */
  y: number;
}

export interface CalculateRealtimeDotOptions {
  /** Visible data points */
  visibleData: (PricePoint | OhlcvPoint)[];
  /** X and Y scales */
  scales: { x: Scale; y: Scale };
  /** Chart layout for bounds checking */
  layout: ChartLayout;
}

// ==================== Direction Color Calculation ====================

/**
 * Calculate the effective line color based on chart mode and price direction.
 * - Single mode: Always use direction-based color (green/red/gray)
 * - Comparison mode: Use provided color prop (palette color)
 */
export function calculateDirectionColor(
  options: DirectionColorOptions
): string {
  const {
    mode,
    visibleData,
    seriesMeta,
    providedColor,
    usePreviousCloseAsBaseline = false,
  } = options;

  // In comparison mode, use series color (palette color)
  if (mode === "comparison") {
    return providedColor ?? DEFAULT_CHART_COLOR;
  }

  // Single mode: compute direction from previousClose to current price
  // Fall back to first visible point if previousClose not available
  const previousClose = seriesMeta?.previousClose;

  if (visibleData.length >= 1) {
    const lastPoint = visibleData.at(-1);

    if (lastPoint) {
      const lastValue = getYValue(lastPoint);

      let baseValue: number;

      if (usePreviousCloseAsBaseline && previousClose !== undefined) {
        baseValue = previousClose;
      } else if (visibleData.length >= 2) {
        const firstPoint = visibleData[0];

        baseValue = firstPoint ? getYValue(firstPoint) : lastValue;
      } else {
        baseValue = lastValue;
      }

      const direction = getPriceDirection(baseValue, lastValue);

      return getDirectionLineColor(direction);
    }
  }

  return CANDLESTICK_COLORS.unchanged;
}

/**
 * Get the baseline value for a series.
 * When usePreviousClose is true and previousClose exists, use previousClose.
 * Otherwise, use the first visible data point's Y value.
 */
export function getBaselineValue(
  visibleData: (PricePoint | OhlcvPoint)[],
  seriesMeta?: TickerSeries,
  usePreviousClose = false
): number | undefined {
  if (visibleData.length === 0) return undefined;

  if (usePreviousClose) {
    const previousClose = seriesMeta?.previousClose;

    if (previousClose !== undefined) return previousClose;
  }

  const firstPoint = visibleData[0];

  return firstPoint ? getYValue(firstPoint) : undefined;
}

// ==================== Realtime Dot Position ====================

/**
 * Calculate the position of the realtime dot at the last data point.
 * Returns null if no data or point is outside visible area.
 */
export function calculateRealtimeDotPosition(
  options: CalculateRealtimeDotOptions
): RealtimeDotPosition | null {
  const { visibleData, scales, layout } = options;

  if (visibleData.length === 0) {
    return null;
  }

  const lastPoint = visibleData.at(-1);

  if (!lastPoint) {
    return null;
  }

  const lastValue = getYValue(lastPoint);
  const x = scales.x.scale(lastPoint.timestamp);
  const y = scales.y.scale(lastValue);

  // Don't render if point is outside visible area
  if (
    x < layout.priceArea.x ||
    x > layout.priceArea.x + layout.priceArea.width
  ) {
    return null;
  }

  return { x, y };
}

// ==================== Gradient Colors ====================

/**
 * Gradient colors for line chart based on price direction.
 * Uses lighter versions of candlestick colors for gradient fill.
 */
export const GRADIENT_COLORS = {
  positive: {
    line: CANDLESTICK_COLORS.up,
  },
  negative: {
    line: CANDLESTICK_COLORS.down,
  },
  neutral: {
    line: CANDLESTICK_COLORS.unchanged,
  },
} as const;

// ==================== Base Value Calculation ====================

/**
 * Calculate the base value for direction comparison.
 * Uses previousClose if available, otherwise uses first visible point.
 */
export function calculateBaseValue(
  visibleData: (PricePoint | OhlcvPoint)[],
  previousClose?: number
): number {
  if (visibleData.length === 0) {
    return 0;
  }

  const lastPoint = visibleData.at(-1);

  if (!lastPoint) {
    return 0;
  }

  const lastValue = getYValue(lastPoint);

  if (previousClose !== undefined) {
    return previousClose;
  }

  if (visibleData.length >= 2) {
    const firstPoint = visibleData[0];

    return firstPoint ? getYValue(firstPoint) : lastValue;
  }

  return lastValue;
}

// ==================== Series Data Utilities ====================

/**
 * Get effective gradient and line color based on price direction.
 */
export function getGradientColors(
  visibleData: (PricePoint | OhlcvPoint)[],
  seriesMeta?: TickerSeries
): { line: string } {
  if (visibleData.length < 1) {
    return GRADIENT_COLORS.neutral;
  }

  const lastPoint = visibleData.at(-1);

  if (!lastPoint) {
    return GRADIENT_COLORS.neutral;
  }

  const lastValue = getYValue(lastPoint);
  const baseValue = calculateBaseValue(visibleData, seriesMeta?.previousClose);
  const direction = getPriceDirection(baseValue, lastValue);

  return GRADIENT_COLORS[direction];
}

/**
 * Check if a point should be rendered as a line chart point.
 * Returns true for line charts (PricePoint or OhlcvPoint used as line).
 */
export function isLineChartData(
  data: (PricePoint | OhlcvPoint)[]
): data is PricePoint[] {
  if (data.length === 0) return true;

  const first = data[0];

  // Check if it's a simple PricePoint (has value property)
  return first !== undefined && "value" in first;
}

/**
 * Extract the display value from a data point.
 * For PricePoint: returns value
 * For OhlcvPoint: returns close
 * Note: This function is imported from pathGenerators and used internally.
 * It is re-exported from core/index.ts via pathGenerators.
 */
